import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { environment } from "../../../../environments/environment";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { firstValueFrom, of } from "rxjs";
import { catchError, delay, first, retry, timeout } from "rxjs/operators";
import { AngularFireAuth } from "@angular/fire/compat/auth";

@Injectable({
  providedIn: "root"
})
export class AuthService {

  // Promesa compartida: llamadas simultáneas esperan al mismo refresco.
  private refreshInProgress$: Promise<string> | null = null;

  constructor(
    private router: Router,
    private http: HttpClient,
    private ngFireAuth: AngularFireAuth
  ) {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") this.refreshIfNeeded();
    });
    window.addEventListener("focus", () => this.refreshIfNeeded());
    this.scheduleProactiveRefresh();
  }

  // Margen (ms) antes de la expiración a partir del cual se renueva el token
  private refreshMarginMs = 5 * 60 * 1000;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  private refreshIfNeeded(): void {
    if (!localStorage.getItem("v2cRefreshToken") && !localStorage.getItem("token")) return;
    const expiresAt = Number(localStorage.getItem("v2cTokenExpiresAt"));
    if (!expiresAt || expiresAt - Date.now() <= this.refreshMarginMs) {
      this.getV2CToken().catch(() => {});
    }
  }

  private scheduleProactiveRefresh(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    const expiresAt = Number(localStorage.getItem("v2cTokenExpiresAt"));
    if (!expiresAt) return;
    const delay = Math.max(expiresAt - this.refreshMarginMs - Date.now(), 0);
    this.refreshTimer = setTimeout(() => {
      this.getV2CToken().catch(() => {});
    }, delay);
  }

  // Método para verificar si el usuario está autenticado
  isAuthenticated(): boolean {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user || !user["email"]) {
      return false;
    } else if (user["email"] == "admin@v2c.com" || user["email"] == "developers@v2c.com" || user["email"] == "support@v2c.com" || user["email"] == "accounting@v2c.com"){
      return false;
    } else {
      return true;
    }
  }

  // Método para redirigir al usuario al login si no está autenticado
  checkAuthentication(): void {
    if (!this.isAuthenticated()) {
      this.router.navigate(["/login"]);
    }
  }

  getV2CToken(forceRefresh = false): Promise<string> {
    if (this.refreshInProgress$) {
      return this.refreshInProgress$;
    }

    this.refreshInProgress$ = this._getV2CToken(forceRefresh).finally(() => {
      this.refreshInProgress$ = null;
    });

    return this.refreshInProgress$;
  }

  private async _getV2CToken(forceRefresh = false): Promise<string> {
    // 1) Token V2C vigente con más de 5 min de vida
    const token = localStorage.getItem("token");
    const expiresAt = Number(localStorage.getItem("v2cTokenExpiresAt"));
    if (!forceRefresh && token && expiresAt && expiresAt - Date.now() > this.refreshMarginMs) {
      return token;
    }

    // 2) refresh_token de V2C
    const storedRefreshToken = localStorage.getItem("v2cRefreshToken");
    if (storedRefreshToken) {
      try {
        const params = new HttpParams()
          .set("grant_type", "refresh_token")
          .set("refresh_token", storedRefreshToken);
        return await this.requestV2CToken(params);
      } catch (refreshError) {
        console.log("El refresh_token ha fallado o ha caducado, se hace fallback a Firebase", refreshError);
      }
    }

    // 3) Firebase: esperar a que se resuelva el estado de auth y forzar un token nuevo
    try {
      await this.waitForAuthState();
      const currentUser = await this.ngFireAuth.currentUser;
      if (currentUser) {
        const firebaseToken = await currentUser.getIdToken(true);
        localStorage.setItem("user", JSON.stringify({ email: currentUser.email, firebaseToken }));

        const params = new HttpParams().set("grant_type", "firebase").set("firebase_token_id", firebaseToken);
        return await this.requestV2CToken(params);
      }
    } catch (error) {
      console.log("ERROR DE AUTENTICACION AL OBTENER TOKEN", error);
    }

    // 4) Todo ha fallado
    this.clearSessionAndRedirect();
    throw new Error("No se pudo renovar la sesión");
  }

  private async waitForAuthState(): Promise<void> {
    await firstValueFrom(this.ngFireAuth.authState.pipe(first(), timeout(10000)));
  }

  private async requestV2CToken(params: HttpParams): Promise<string> {
    const header = new HttpHeaders().set("Authorization", "Basic " + environment.v2cServiceConfig.basicOAuth);
    const response: any = await firstValueFrom(
      this.http.post(environment.v2cServiceConfig.authBasePath + "/oauth/token", params, { headers: header })
    );
    if (!response?.access_token) {
      throw new Error("Respuesta de token inválida");
    }
    this.saveTokenResponse(response);
    return response.access_token;
  }

  clearSessionAndRedirect(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    ["token", "user", "v2cRefreshToken", "v2cTokenExpiresAt", "refreshingV2CToken"]
      .forEach(k => localStorage.removeItem(k));
    this.router.navigate(["/login"]);
  }

  // Guarda access_token, refresh_token y el instante de caducidad
  private saveTokenResponse(response: any) {
    if (response?.access_token) {
      localStorage.setItem("token", response.access_token);
    }
    if (response?.refresh_token) {
      localStorage.setItem("v2cRefreshToken", response.refresh_token);
    }
    if (response?.expires_in) {
      const lifetimeMs = response.expires_in * 1000;
      this.refreshMarginMs = Math.min(5 * 60 * 1000, lifetimeMs / 2);
      localStorage.setItem("v2cTokenExpiresAt", String(Date.now() + lifetimeMs));
    }
    this.scheduleProactiveRefresh();
  }

  async getUser() {
    try {
      const user = await firstValueFrom(
        this.ngFireAuth.authState.pipe(
          first(),
          timeout(4000),
          retry({
            count: 3, // Reintenta hasta 3 veces
            delay: (error, retryCount) => {
              console.log(`Retry attempt #${retryCount}`);
              return of(error).pipe(delay(1000 * retryCount)); // Backoff exponencial
            }
          }),
          catchError(error => {
            console.error("El metodo GETUSER ha fallado, el internet no funciona bien", error);
            return of(null);
          })
        )
      );

      if (user) {
        const email = user.email;
        const firebaseToken = await user.getIdToken(true);
        const localUser = {
          email: email,
          firebaseToken: firebaseToken
        };

        localStorage.setItem("user", JSON.stringify(localUser));
        return localUser;
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error al obtener el usuario:", error);
      return null;
    }
  }
}